import React from 'react';
import { ConsultantNode, PayoutBreakdown } from '../../types/career';
import { MetricCard } from './MetricCard';
import { Badge } from '../ui/Badge';
import { formatAE, formatCurrencyUAH, formatPercent } from '../../utils/formatters';
import { getLevelDefinition, getLevelTitle } from '../../math/careerRules';
import { countActiveConsultants, countTotalConsultants } from '../../utils/treeUtils';
import {
  Award,
  Wallet,
  TrendingUp,
  Layers,
  Activity,
  Users,
} from 'lucide-react';

export interface MetricsGridProps {
  rootNode: ConsultantNode;
  payout: PayoutBreakdown;
  activeCount?: number;
  totalCount?: number;
  previousMonthGrossIncome?: number;
  className?: string;
}

export const MetricsGrid: React.FC<MetricsGridProps> = ({
  rootNode,
  payout,
  activeCount,
  totalCount,
  previousMonthGrossIncome,
  className = '',
}) => {
  const level = rootNode.level;
  const levelTitle = getLevelTitle(level);

  const calculatedTotalCount = totalCount ?? countTotalConsultants(rootNode);
  const calculatedActiveCount = activeCount ?? countActiveConsultants(rootNode);
  const dormantCount = Math.max(0, calculatedTotalCount - calculatedActiveCount);
  const activePercent = calculatedTotalCount > 0 ? calculatedActiveCount / calculatedTotalCount : 1;

  // Next level progress
  const nextLevel = level < 8 ? ((level + 1) as typeof level) : null;
  const nextDef = nextLevel ? getLevelDefinition(nextLevel) : null;
  const progressToNext = nextDef
    ? Math.min(100, Math.round((rootNode.totalCumulativeAE / nextDef.minCumulativeUnits) * 100))
    : 100;

  // Trend calculation
  let trendInfo: { value: string; isPositive: boolean } | undefined;
  if (previousMonthGrossIncome !== undefined && previousMonthGrossIncome > 0) {
    const diff = payout.totalGrossIncomeUAH - previousMonthGrossIncome;
    const pct = (diff / previousMonthGrossIncome) * 100;
    const isPositive = diff >= 0;
    trendInfo = {
      value: `${isPositive ? '+' : ''}${pct.toFixed(1)}%`,
      isPositive,
    };
  }

  const teamMonthlyAE =
    rootNode.teamMonthlyAE !== undefined
      ? rootNode.teamMonthlyAE
      : Math.max(0, rootNode.totalMonthlyAE - (rootNode.isActive ? rootNode.personalMonthlyAE : 0));

  return (
    <div
      className={`grid grid-cols-2 gap-2.5 sm:gap-3 ${className}`}
    >
      {/* 1. Кар'єрний рівень */}
      <MetricCard
        title="Рівень"
        value={`Рівень ${level}`}
        subValue={levelTitle}
        icon={<Award className="w-3.5 h-3.5" />}
        iconColor="text-amber-400"
        badge={
          <Badge level={level} size="sm">
            Р{level}
          </Badge>
        }
      />

      {/* 2. Місячний чек (Брутто & Нетто) */}
      <MetricCard
        title="Місячний чек"
        value={formatCurrencyUAH(payout.totalGrossIncomeUAH, 0)}
        subValue={`Чистими: ${formatCurrencyUAH(payout.estimatedNetIncomeUAH, 0)}`}
        icon={<Wallet className="w-3.5 h-3.5" />}
        iconColor="text-accent-neon"
        trend={trendInfo}
        highlight={true}
      />

      {/* 3. Місячний обіг (Е) */}
      <MetricCard
        title="Обіг за місяць"
        value={formatAE(rootNode.totalMonthlyAE, 0)}
        subValue={`Власні: ${formatAE(rootNode.isActive ? rootNode.personalMonthlyAE : 0, 0)} | Команда: ${formatAE(teamMonthlyAE, 0)}`}
        icon={<TrendingUp className="w-3.5 h-3.5" />}
        iconColor="text-cyan-400"
      />

      {/* 4. Загальні накопичені Е */}
      <MetricCard
        title="Накопичені Е"
        value={formatAE(rootNode.totalCumulativeAE, 0)}
        subValue={
          nextDef
            ? `До Р${nextLevel}: ${progressToNext}% (бракує ${formatAE(Math.max(0, nextDef.minCumulativeUnits - rootNode.totalCumulativeAE), 0)})`
            : 'Найвищий кар\'єрний ранг'
        }
        icon={<Layers className="w-3.5 h-3.5" />}
        iconColor="text-purple-400"
      />

      {/* 5. Активні партнери */}
      <MetricCard
        title="Активні"
        value={`${calculatedActiveCount} / ${calculatedTotalCount}`}
        subValue={`Неактивні: ${dormantCount} (${formatPercent(activePercent, 0)})`}
        icon={<Activity className="w-3.5 h-3.5" />}
        iconColor="text-emerald-400"
      />

      {/* 6. Загалом у команді */}
      <MetricCard
        title="Вся структура"
        value={`${calculatedTotalCount} чол.`}
        subValue={`Прямих ліній: ${rootNode.children.length}`}
        icon={<Users className="w-3.5 h-3.5" />}
        iconColor="text-sky-400"
      />
    </div>
  );
};
